/**
 * Main-thread side of the sim: owns the Worker, decodes snapshots into two
 * persistent slots, interpolates between them each frame, and writes the
 * result into the renderers. Also relays verbs in and sim events out.
 */
import {
  DecodedSnapshot,
  SCALE_Q,
  decodeSnapshotInto,
  type MainToWorker,
  type SimEvent,
  type WorkerToMain,
} from '../sim/protocol';
import { PARAMS } from '../sim/params';
import { terrainHeight } from '../sim/terrain';
import type { CreatureRenderer } from '../render/creatures';
import type { ItemRenderer } from '../render/items';
import type { AwayReport, SaveV1 } from '../save/schema';
import type { InspectData, WorkerStats } from '../sim/protocol';

const TWO_PI = Math.PI * 2;
const TICK_DUR_MS = PARAMS.TICK_MS;

export class SimClient {
  private readonly worker: Worker;
  private older = new DecodedSnapshot();
  private newer = new DecodedSnapshot();
  stats: WorkerStats = {
    t: 'stats',
    tick: 0,
    pop: 0,
    tickMs: 0,
    stage: 0,
    trust: 0.4,
    attachment: 0.3,
    curiosity: 0.5,
    fairness: 0.5,
  };
  snapshotAgeMs = 0;
  onEvents: ((events: SimEvent[]) => void) | null = null;
  onSave: ((save: SaveV1) => void) | null = null;
  onAway: ((report: AwayReport) => void) | null = null;
  onGrid: ((grid: Uint8Array) => void) | null = null;
  onInspect: ((data: InspectData) => void) | null = null;

  constructor(
    seed: string,
    initialCount: number,
    save: SaveV1 | null = null,
    now = 0,
    startPaused = false,
  ) {
    this.worker = new Worker(new URL('../sim/worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (e: MessageEvent) => {
      if (e.data instanceof ArrayBuffer) {
        this.onSnapshot(e.data);
        return;
      }
      const msg = e.data as WorkerToMain;
      if (msg.t === 'stats') this.stats = msg;
      else if (msg.t === 'ev' && this.onEvents) this.onEvents(msg.l);
      else if (msg.t === 'savePayload' && this.onSave) this.onSave(msg.save);
      else if (msg.t === 'away' && this.onAway) this.onAway(msg.report);
      else if (msg.t === 'grid' && this.onGrid) this.onGrid(new Uint8Array(msg.buf));
      else if (msg.t === 'inspectData' && this.onInspect) this.onInspect(msg);
    };
    this.send({ t: 'init', seed, count: initialCount, save, now, startPaused });
  }

  requestSave(now: number): void {
    this.send({ t: 'requestSave', now });
  }

  private send(msg: MainToWorker, transfer?: Transferable[]): void {
    if (transfer) this.worker.postMessage(msg, transfer);
    else this.worker.postMessage(msg);
  }

  private onSnapshot(buf: ArrayBuffer): void {
    // decode into the older slot, then swap — buffer goes straight back
    const dst = this.older;
    decodeSnapshotInto(dst, buf, performance.now());
    this.send({ t: 'bufback', buf }, [buf]);
    this.older = this.newer;
    this.newer = dst;
  }

  // ---- verbs ----

  tapEgg(x: number, z: number): void {
    this.send({ t: 'verb', kind: 'tapEgg', x, z });
  }

  dropApple(x: number, z: number): void {
    this.send({ t: 'verb', kind: 'apple', x, z });
  }

  ballPlace(x: number, z: number): void {
    this.send({ t: 'verb', kind: 'ballPlace', x, z });
  }

  ballThrow(vx: number, vz: number, power: number): void {
    this.send({ t: 'verb', kind: 'ballThrow', vx, vz, power });
  }

  pet(id: number): void {
    this.send({ t: 'verb', kind: 'pet', id });
  }

  beacon(x: number, z: number): void {
    this.send({ t: 'verb', kind: 'beacon', x, z });
  }

  stroke(id: number): void {
    this.send({ t: 'verb', kind: 'stroke', id });
  }

  song(x: number, z: number): void {
    this.send({ t: 'verb', kind: 'song', x, z });
  }

  scrub(points: { x: number; z: number }[]): void {
    if (points.length > 0) this.send({ t: 'scrub', points });
  }

  nudge(kind: 'hunger' | 'grime' | 'ball'): void {
    this.send({ t: 'nudge', kind });
  }

  answer(qid: string, choice: 0 | 1): void {
    this.send({ t: 'answer', qid, choice });
  }

  inspect(id: number): void {
    this.send({ t: 'inspect', id });
  }

  setPopulation(count: number): void {
    this.send({ t: 'setPop', count });
  }

  /** Debug: run the sim at mult x real time. */
  warp(mult: number): void {
    this.send({ t: 'warp', mult });
  }

  /** Debug: jump hours ahead through the offline macro sim. */
  skip(hours: number): void {
    this.send({ t: 'skip', hours });
  }

  // ---- reads ----

  get population(): number {
    return this.newer.count;
  }

  get dayPhase(): number {
    return this.newer.dayPhase;
  }

  /** Count of creatures currently in a given life state (newest snapshot). */
  countLife(state: number): number {
    let n = 0;
    for (let i = 0; i < this.newer.count; i++) {
      if (this.newer.life[i] === state) n++;
    }
    return n;
  }

  /** Newest known position of the first item of a type, or null. */
  findItem(type: number): { id: number; x: number; z: number } | null {
    const s = this.newer;
    for (let i = 0; i < s.itemCount; i++) {
      if (s.itemType[i] === type) {
        return { id: s.itemId[i] as number, x: s.itemX[i] as number, z: s.itemZ[i] as number };
      }
    }
    return null;
  }

  /** Nearest creature to a ground point within maxDist, or -1 (fat-finger picking). */
  nearestCreature(x: number, z: number, maxDist: number): number {
    const s = this.newer;
    let best = -1;
    let bestD2 = maxDist * maxDist;
    for (let i = 0; i < s.count; i++) {
      const dx = (s.x[i] as number) - x;
      const dz = (s.z[i] as number) - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    return best;
  }

  /** Count of items of a type in the newest snapshot. */
  countItems(type: number): number {
    const s = this.newer;
    let n = 0;
    for (let i = 0; i < s.itemCount; i++) if (s.itemType[i] === type) n++;
    return n;
  }

  /** Distance from (x,z) to the nearest item of a type, Infinity if none. */
  distToItem(type: number, x: number, z: number): number {
    const s = this.newer;
    let best = Infinity;
    for (let i = 0; i < s.itemCount; i++) {
      if (s.itemType[i] !== type) continue;
      const d = Math.hypot((s.itemX[i] as number) - x, (s.itemZ[i] as number) - z);
      if (d < best) best = d;
    }
    return best;
  }

  /** Interpolate S1->S2 and write into the renderers. */
  applyTo(creatures: CreatureRenderer, items: ItemRenderer | null, nowMs: number, dt: number): void {
    const s1 = this.older;
    const s2 = this.newer;
    if (s2.tick < 0) return; // nothing yet
    this.snapshotAgeMs = nowMs - s2.arrivalMs;

    const haveTwo = s1.tick >= 0 && s1.tick < s2.tick;
    const alpha = haveTwo
      ? Math.min(1.2, Math.max(0, (nowMs - s2.arrivalMs + TICK_DUR_MS) / ((s2.tick - s1.tick) * TICK_DUR_MS)))
      : 1;

    const n = s2.count;
    creatures.setCount(n);
    for (let i = 0; i < n; i++) {
      let x = s2.x[i] as number;
      let z = s2.z[i] as number;
      let h = s2.heading[i] as number;
      if (haveTwo && i < s1.count) {
        const x1 = s1.x[i] as number;
        const z1 = s1.z[i] as number;
        x = x1 + (x - x1) * alpha;
        z = z1 + (z - z1) * alpha;
        const h1 = s1.heading[i] as number;
        let dh = h - h1;
        while (dh > Math.PI) dh -= TWO_PI;
        while (dh < -Math.PI) dh += TWO_PI;
        h = h1 + dh * alpha;
      }
      creatures.setInstance(i, {
        x,
        y: terrainHeight(x, z),
        z,
        heading: h,
        anim: s2.anim[i] as number,
        emotion: (s2.emotion[i] as number) / 255,
        scale: (s2.scaleQ[i] as number) / SCALE_Q,
        seed: (s2.seed[i] as number) / 255,
        phase: (s2.phase[i] as number) / 255,
        hue: (s2.hue[i] as number) / 255,
      });
    }
    creatures.commit();
    if (items) items.applyFrom(s1, s2, alpha, dt);
  }

  pause(): void {
    this.send({ t: 'pause' });
  }

  resume(): void {
    this.send({ t: 'resume' });
  }
}
